import cn from 'classnames';
import React, { useEffect } from 'react';

import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import hasSidebarTab from 'editor/src/store/editorModules/sidebar/selector/hasSidebarTab';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import limitPrecision from 'editor/src/util/limitPrecision';
import useMediaElementLiveUpdates from 'editor/src/util/useMediaElementLiveUpdates';

import IndicatorBadge from 'editor/src/component/IndicatorBadge';
import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';

import useElementWarnings from './useElementWarnings';

import styles from './index.module.scss';

export const canShow: CanShow = (state, { hasSelection }) => {
  // GAPI-53417: on a gang sheet this cell also carries the artwork's size, which the
  // buyer needs whether or not the product has a warnings tab
  return hasSelection && (hasSidebarTab(state, TAB_NAMES.WARNINGS) || !!state.hostSettings.gangSheet);
};

type Props = Pick<MenuItemProps, 'context' | 'close' | 'refresh'>;

function ButtonWarnings({ context, close, refresh }: Props) {
  const dispatch = useDispatch();
  const element = useSelector((state) => getMediaElement(state, context.structureIndexes[0]));
  const { liveElement } = useMediaElementLiveUpdates(element);
  // GAPI-53417: the gang sheet block is this line's only opt-in
  const isGangSheet = useSelector((state) => !!state.hostSettings.gangSheet);

  const handleClick = () => {
    if (level) {
      dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.WARNINGS));
      close?.();
    }
  };

  const { level, tooltipLabel, warnings, text } = useElementWarnings(liveElement);

  // Sits under the badge rather than beside it: the toolbar lays its items out by
  // measured width and moves whatever no longer fits into the overflow menu, so a second
  // line costs nothing while a second pill could push a real button out of reach. The
  // bar is a fixed 56px, so the two lines change no heights.
  const sizeLabel =
    isGangSheet && liveElement && liveElement.type !== 'line'
      ? `${limitPrecision(liveElement.width, 1)} × ${limitPrecision(liveElement.height, 1)} mm`
      : undefined;

  // the toolbar positions its items by measured width, so it has to re-measure whenever
  // this cell's width can change. Keyed on the label's length rather than the label:
  // during a resize drag the millimetres change on nearly every frame while the width
  // only moves when a digit is gained or lost.
  useEffect(() => {
    refresh?.();
  }, [warnings.length, sizeLabel?.length]);

  if (level || tooltipLabel || sizeLabel) {
    return (
      <div
        onClick={handleClick}
        className={cn(styles.ButtonWarning, 'cy-button-warnings', `cy-warning-level-${level || 'None'}`, {
          [styles.button]: !!level,
        })}
      >
        {(level || tooltipLabel) && (
          <IndicatorBadge className={styles.indicator} tooltip={tooltipLabel} level={level} text={text} />
        )}
        {sizeLabel && <div className={cn(styles.size, 'cy-element-size')}>{sizeLabel}</div>}
      </div>
    );
  }

  return null;
}

export default React.memo(ButtonWarnings);
